export type ToneType = keyof typeof toneDefinitions;
export type OfferType = keyof typeof offerDefinitions;

export interface FormDefinition {
  id: string;
  title: string;
  description: string;
  fields: {
    id: string;
    label: string;
    type: string;
    required?: boolean;
    placeholder?: string;
    options?: Array<{
      value: string;
      label: string;
    }>;
    dependsOn?: string;
    showIf?: (values: Record<string, any>) => boolean;
  }[];
}

export const toneDefinitions = {
  spartan: "direct and no-nonsense, cutting straight to the point without fluff or unnecessary pleasantries",
  slytherin: "subtly manipulative and cunningly persuasive, using sophisticated language that appeals to ambition and self-interest, while maintaining an air of exclusivity and refined authority. Each word is carefully chosen to create a sense of insider knowledge and strategic advantage",
  professional: "polished and formal, maintaining appropriate business etiquette while being clear and concise",
  friendly: "warm and approachable, using conversational language that builds rapport and trust",
  enthusiastic: "energetic and passionate, using dynamic language that conveys excitement and possibility",
  authoritative: "expert and commanding, demonstrating deep knowledge and leadership in the industry"
} as const;

export interface OfferDefinition {
  name: string;
  description: string;
  components: string[];
  valueProps: string[];
}

export const offerDefinitions: Record<string, OfferDefinition> = {
  ppc: {
    name: "PPC Management",
    description: "Professional pay-per-click advertising management service",
    components: [
      "Campaign strategy development",
      "Keyword research and optimization",
      "Ad copy creation and testing",
      "Landing page optimization",
      "Performance tracking and reporting"
    ],
    valueProps: [
      "Increase qualified leads while reducing ad spend",
      "Data-driven optimization for better ROI",
      "Expert management of your advertising budget",
      "Continuous testing and improvement"
    ]
  },
  cold_email: {
    name: "Cold Email Lead Generation",
    description: "Done-for-you cold email outreach and lead generation service",
    components: [
      "Prospect list building",
      "Email sequence creation",
      "Campaign management",
      "Response handling",
      "Lead qualification"
    ],
    valueProps: [
      "Generate qualified leads without cold calling",
      "Scale your outreach efforts",
      "Build a predictable sales pipeline",
      "Focus on closing while we handle prospecting"
    ]
  },
  app_dev: {
    name: "Application Development",
    description: "Custom software and application development solutions",
    components: [
      "Requirements analysis",
      "UI/UX design",
      "Full-stack development",
      "Testing and QA",
      "Deployment and maintenance"
    ],
    valueProps: [
      "Custom solutions tailored to your business",
      "Modern, scalable architecture",
      "Ongoing technical support",
      "Integration with existing systems"
    ]
  },
  ai_automation: {
    name: "AI Automations",
    description: "AI-powered automation solutions for business processes",
    components: [
      "Process analysis and optimization",
      "AI model development",
      "Workflow automation",
      "Integration with existing tools",
      "Performance monitoring"
    ],
    valueProps: [
      "Reduce manual work by up to 80%",
      "Improve accuracy and consistency",
      "Scale operations without adding headcount",
      "24/7 automated operations"
    ]
  },
  seo: {
    name: "SEO Strategy & Implementation",
    description: "Comprehensive search engine optimization services to improve organic visibility",
    components: [
      "Technical SEO audit",
      "Keyword research and strategy",
      "On-page optimization",
      "Content strategy",
      "Link building",
      "Performance monitoring"
    ],
    valueProps: [
      "Increase organic search visibility",
      "Drive sustainable traffic growth",
      "Improve domain authority",
      "Outrank competitors for key terms"
    ]
  },
  social_media: {
    name: "Social Media Management",
    description: "Full-service social media management and growth strategy",
    components: [
      "Platform strategy development",
      "Content calendar creation",
      "Daily post management",
      "Community engagement",
      "Analytics and reporting"
    ],
    valueProps: [
      "Build brand awareness and engagement",
      "Consistent brand voice across platforms",
      "Grow your social following",
      "Convert followers into customers"
    ]
  },
  content_marketing: {
    name: "Content Marketing",
    description: "Strategic content creation and distribution to attract and retain customers",
    components: [
      "Content strategy development",
      "Editorial calendar planning",
      "Content creation",
      "Distribution strategy",
      "Performance analysis"
    ],
    valueProps: [
      "Establish thought leadership",
      "Drive organic traffic growth",
      "Build brand authority",
      "Generate quality leads"
    ]
  },
  branding: {
    name: "Branding & Design",
    description: "Comprehensive branding and design services to build a strong visual identity",
    components: [
      "Brand strategy development",
      "Visual identity creation",
      "Brand guidelines",
      "Marketing collateral design",
      "Brand voice development"
    ],
    valueProps: [
      "Stand out from competitors",
      "Build brand recognition",
      "Maintain consistency across channels",
      "Connect emotionally with audiences"
    ]
  },
  cro: {
    name: "Conversion Rate Optimization",
    description: "Data-driven optimization to increase website and funnel conversions",
    components: [
      "User behavior analysis",
      "A/B testing",
      "Landing page optimization",
      "Funnel analysis",
      "Implementation of best practices"
    ],
    valueProps: [
      "Increase conversion rates",
      "Improve ROI on traffic",
      "Optimize user experience",
      "Data-backed improvements"
    ]
  },
  paid_social: {
    name: "Paid Social Ads",
    description: "Strategic social media advertising across major platforms",
    components: [
      "Platform selection strategy",
      "Ad creative development",
      "Audience targeting",
      "Campaign management",
      "Performance optimization"
    ],
    valueProps: [
      "Reach targeted audiences",
      "Scale customer acquisition",
      "Improve ad ROI",
      "Build brand awareness"
    ]
  },
  youtube_ads: {
    name: "YouTube Ads & Video Marketing",
    description: "Comprehensive video marketing and YouTube advertising services",
    components: [
      "Video strategy development",
      "Ad creative production",
      "Campaign setup and management",
      "Audience targeting",
      "Performance optimization"
    ],
    valueProps: [
      "Engage audiences with video",
      "Build brand awareness",
      "Drive qualified traffic",
      "Reach targeted viewers"
    ]
  },
  email_marketing: {
    name: "Email Marketing",
    description: "Strategic email marketing services to nurture and convert leads",
    components: [
      "Email strategy development",
      "Campaign creation",
      "List segmentation",
      "Automation setup",
      "Performance tracking"
    ],
    valueProps: [
      "Nurture leads effectively",
      "Increase customer lifetime value",
      "Automate customer communication",
      "Drive repeat sales"
    ]
  },
  influencer_marketing: {
    name: "Influencer Marketing",
    description: "Strategic influencer partnerships and campaign management",
    components: [
      "Influencer identification",
      "Partnership negotiation",
      "Campaign strategy",
      "Content collaboration",
      "Performance tracking"
    ],
    valueProps: [
      "Reach engaged audiences",
      "Build social proof",
      "Create authentic content",
      "Drive brand awareness"
    ]
  },
  web_development: {
    name: "Web & Funnel Development",
    description: "Custom website and sales funnel development solutions",
    components: [
      "Website design",
      "Funnel architecture",
      "Development and testing",
      "Integration setup",
      "Performance optimization"
    ],
    valueProps: [
      "Convert more visitors",
      "Improve user experience",
      "Scale your business",
      "Technical excellence"
    ]
  },
  reputation: {
    name: "Reputation Management",
    description: "Comprehensive online reputation management services",
    components: [
      "Review monitoring",
      "Response management",
      "Proactive reputation building",
      "Crisis management",
      "Brand monitoring"
    ],
    valueProps: [
      "Build trust with customers",
      "Manage online perception",
      "Address negative feedback",
      "Strengthen brand reputation"
    ]
  },
  marketing_automation: {
    name: "Marketing Automation & CRM",
    description: "Implementation and management of marketing automation and CRM systems",
    components: [
      "System selection and setup",
      "Workflow automation",
      "Integration configuration",
      "Training and support",
      "Optimization and maintenance"
    ],
    valueProps: [
      "Automate repetitive tasks",
      "Improve lead management",
      "Increase team efficiency",
      "Better customer insights"
    ]
  },
  pr: {
    name: "Public Relations",
    description: "Strategic public relations services to build brand awareness and credibility",
    components: [
      "PR strategy development",
      "Media relations",
      "Press release creation",
      "Event planning",
      "Crisis communication"
    ],
    valueProps: [
      "Build media presence",
      "Enhance brand credibility",
      "Manage public perception",
      "Create newsworthy stories"
    ]
  },
  ecommerce: {
    name: "E-Commerce Optimization",
    description: "Comprehensive e-commerce optimization services",
    components: [
      "Platform optimization",
      "Product page optimization",
      "Checkout optimization",
      "Analytics setup",
      "Conversion tracking"
    ],
    valueProps: [
      "Increase online sales",
      "Improve shopping experience",
      "Reduce cart abandonment",
      "Optimize product visibility"
    ]
  }
} as const;

export const industries = [
  { value: 'accountants', label: 'Accountants' },
  { value: 'auto_mechanics', label: 'Auto Mechanics' },
  { value: 'business_brokers', label: 'Business Brokers' },
  { value: 'car_dealerships', label: 'Car Dealerships' },
  { value: 'chiropractors', label: 'Chiropractors' },
  { value: 'colleges_universities', label: 'Colleges & Universities' },
  { value: 'cybersecurity_firms', label: 'Cybersecurity Firms' },
  { value: 'dentists', label: 'Dentists' },
  { value: 'doctors', label: 'Doctors' },
  { value: 'ecommerce_store_owners', label: 'E-commerce Store Owners' },
  { value: 'electricians', label: 'Electricians' },
  { value: 'event_wedding_planners', label: 'Event & Wedding Planners' },
  { value: 'financial_advisors', label: 'Financial Advisors' },
  { value: 'freight_logistics', label: 'Freight & Logistics' },
  { value: 'hair_salons_spas', label: 'Hair Salons & Spas' },
  { value: 'hospitality', label: 'Hospitality (Hotels & Resorts)' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'insurance_agents', label: 'Insurance Agents' },
  { value: 'landscapers', label: 'Landscapers' },
  { value: 'lawyers', label: 'Lawyers' },
  { value: 'ma', label: 'M&A' },
  { value: 'manufacturing_supply_chain', label: 'Manufacturing & Supply Chain' },
  { value: 'marketing_agencies', label: 'Marketing Agencies' },
  { value: 'mortgage_brokers', label: 'Mortgage Brokers' },
  { value: 'nonprofits', label: 'Nonprofits' },
  { value: 'personal_trainers', label: 'Personal Trainers' },
  { value: 'pest_control_services', label: 'Pest Control Services' },
  { value: 'pet_groomers', label: 'Pet Groomers' },
  { value: 'plumbers', label: 'Plumbers' },
  { value: 'real_estate_agents', label: 'Real Estate Agents' },
  { value: 'restaurants', label: 'Restaurants' },
  { value: 'retail_stores', label: 'Retail Stores' },
  { value: 'roofers', label: 'Roofers' },
  { value: 'saas_startups', label: 'SaaS Startups' },
  { value: 'mental_health_therapists', label: 'Mental Health Therapists' },
  { value: 'veterinarians', label: 'Veterinarians' }
];

export const toneOptions = [
  { value: 'spartan', label: 'Spartan - No Bullshit (recommended)' },
  { value: 'slytherin', label: 'Slytherin - Calculated & Persuasive' },
  { value: 'professional', label: 'Professional - Polished & Formal' },
  { value: 'friendly', label: 'Friendly - Warm & Approachable' },
  { value: 'enthusiastic', label: 'Enthusiastic - Energetic & Passionate' },
  { value: 'authoritative', label: 'Authoritative - Expert & Commanding' }
];

export const offerTypes = [
  { value: 'custom', label: 'Custom Offer' },
  { value: 'ppc', label: 'PPC Management' },
  { value: 'cold_email', label: 'Cold Email Lead Gen' },
  { value: 'app_dev', label: 'Application Development' },
  { value: 'ai_automation', label: 'AI Automations' },
  { value: 'seo', label: 'SEO Strategy & Implementation' },
  { value: 'social_media', label: 'Social Media Management' },
  { value: 'content_marketing', label: 'Content Marketing' },
  { value: 'branding', label: 'Branding & Design' },
  { value: 'cro', label: 'Conversion Rate Optimization' },
  { value: 'paid_social', label: 'Paid Social Ads' },
  { value: 'youtube_ads', label: 'YouTube Ads & Video Marketing' },
  { value: 'email_marketing', label: 'Email Marketing' },
  { value: 'influencer_marketing', label: 'Influencer Marketing' },
  { value: 'web_development', label: 'Web & Funnel Development' },
  { value: 'reputation', label: 'Reputation Management' },
  { value: 'marketing_automation', label: 'Marketing Automation & CRM' },
  { value: 'pr', label: 'Public Relations' },
  { value: 'ecommerce', label: 'E-Commerce Optimization' }
];

// Industry-specific roles mapping
export const industryRoles: Record<string, Array<{ value: string, label: string }>> = {
  accountants: [
    { value: 'owner', label: 'Firm Owner' },
    { value: 'partner', label: 'Partner' },
    { value: 'cpa', label: 'CPA' },
    { value: 'tax_manager', label: 'Tax Manager' },
    { value: 'audit_manager', label: 'Audit Manager' }
  ],
  auto_mechanics: [
    { value: 'shop_owner', label: 'Shop Owner' },
    { value: 'service_manager', label: 'Service Manager' },
    { value: 'master_mechanic', label: 'Master Mechanic' },
    { value: 'fleet_manager', label: 'Fleet Manager' }
  ],
  business_brokers: [
    { value: 'broker_owner', label: 'Brokerage Owner' },
    { value: 'senior_broker', label: 'Senior Broker' },
    { value: 'ma_advisor', label: 'M&A Advisor' },
    { value: 'business_valuator', label: 'Business Valuator' }
  ],
  car_dealerships: [
    { value: 'dealer_principal', label: 'Dealer Principal' },
    { value: 'general_manager', label: 'General Manager' },
    { value: 'sales_manager', label: 'Sales Manager' },
    { value: 'finance_manager', label: 'Finance Manager' },
    { value: 'service_director', label: 'Service Director' }
  ],
  chiropractors: [
    { value: 'practice_owner', label: 'Practice Owner' },
    { value: 'clinic_director', label: 'Clinic Director' },
    { value: 'lead_chiropractor', label: 'Lead Chiropractor' },
    { value: 'wellness_coordinator', label: 'Wellness Coordinator' }
  ],
  colleges_universities: [
    { value: 'president', label: 'President' },
    { value: 'dean', label: 'Dean' },
    { value: 'department_head', label: 'Department Head' },
    { value: 'admissions_director', label: 'Admissions Director' },
    { value: 'research_director', label: 'Research Director' }
  ],
  cybersecurity_firms: [
    { value: 'ceo', label: 'CEO' },
    { value: 'ciso', label: 'CISO' },
    { value: 'security_director', label: 'Security Director' },
    { value: 'solutions_architect', label: 'Solutions Architect' },
    { value: 'security_consultant', label: 'Security Consultant' }
  ],
  dentists: [
    { value: 'practice_owner', label: 'Practice Owner' },
    { value: 'dental_director', label: 'Dental Director' },
    { value: 'lead_dentist', label: 'Lead Dentist' },
    { value: 'office_manager', label: 'Office Manager' }
  ],
  doctors: [
    { value: 'practice_owner', label: 'Practice Owner' },
    { value: 'medical_director', label: 'Medical Director' },
    { value: 'chief_physician', label: 'Chief Physician' },
    { value: 'practice_manager', label: 'Practice Manager' }
  ],
  ecommerce_store_owners: [
    { value: 'owner', label: 'Store Owner' },
    { value: 'ceo', label: 'CEO' },
    { value: 'ecommerce_manager', label: 'E-commerce Manager' },
    { value: 'operations_director', label: 'Operations Director' }
  ],
  electricians: [
    { value: 'company_owner', label: 'Company Owner' },
    { value: 'master_electrician', label: 'Master Electrician' },
    { value: 'operations_manager', label: 'Operations Manager' },
    { value: 'project_manager', label: 'Project Manager' }
  ],
  event_wedding_planners: [
    { value: 'agency_owner', label: 'Agency Owner' },
    { value: 'senior_planner', label: 'Senior Planner' },
    { value: 'creative_director', label: 'Creative Director' },
    { value: 'event_coordinator', label: 'Event Coordinator' }
  ],
  financial_advisors: [
    { value: 'firm_owner', label: 'Firm Owner' },
    { value: 'senior_advisor', label: 'Senior Advisor' },
    { value: 'wealth_manager', label: 'Wealth Manager' },
    { value: 'portfolio_manager', label: 'Portfolio Manager' }
  ],
  freight_logistics: [
    { value: 'company_owner', label: 'Company Owner' },
    { value: 'operations_director', label: 'Operations Director' },
    { value: 'fleet_manager', label: 'Fleet Manager' },
    { value: 'logistics_manager', label: 'Logistics Manager' }
  ],
  hair_salons_spas: [
    { value: 'salon_owner', label: 'Salon Owner' },
    { value: 'spa_director', label: 'Spa Director' },
    { value: 'creative_director', label: 'Creative Director' },
    { value: 'location_manager', label: 'Location Manager' }
  ],
  hospitality: [
    { value: 'hotel_owner', label: 'Hotel Owner' },
    { value: 'general_manager', label: 'General Manager' },
    { value: 'operations_director', label: 'Operations Director' },
    { value: 'revenue_manager', label: 'Revenue Manager' }
  ],
  hvac: [
    { value: 'company_owner', label: 'Company Owner' },
    { value: 'service_manager', label: 'Service Manager' },
    { value: 'operations_manager', label: 'Operations Manager' },
    { value: 'project_manager', label: 'Project Manager' }
  ],
  insurance_agents: [
    { value: 'agency_owner', label: 'Agency Owner' },
    { value: 'senior_agent', label: 'Senior Agent' },
    { value: 'sales_manager', label: 'Sales Manager' },
    { value: 'claims_manager', label: 'Claims Manager' }
  ],
  landscapers: [
    { value: 'company_owner', label: 'Company Owner' },
    { value: 'operations_manager', label: 'Operations Manager' },
    { value: 'project_manager', label: 'Project Manager' },
    { value: 'design_director', label: 'Design Director' }
  ],
  lawyers: [
    { value: 'managing_partner', label: 'Managing Partner' },
    { value: 'senior_partner', label: 'Senior Partner' },
    { value: 'practice_head', label: 'Practice Head' },
    { value: 'legal_director', label: 'Legal Director' }
  ],
  ma: [
    { value: 'managing_director', label: 'Managing Director' },
    { value: 'partner', label: 'Partner' },
    { value: 'principal', label: 'Principal' },
    { value: 'senior_associate', label: 'Senior Associate' }
  ],
  manufacturing_supply_chain: [
    { value: 'ceo', label: 'CEO' },
    { value: 'operations_director', label: 'Operations Director' },
    { value: 'plant_manager', label: 'Plant Manager' },
    { value: 'supply_chain_director', label: 'Supply Chain Director' }
  ],
  marketing_agencies: [
    { value: 'agency_owner', label: 'Agency Owner' },
    { value: 'ceo', label: 'CEO' },
    { value: 'creative_director', label: 'Creative Director' },
    { value: 'client_services_director', label: 'Client Services Director' }
  ],
  mortgage_brokers: [
    { value: 'broker_owner', label: 'Broker Owner' },
    { value: 'senior_broker', label: 'Senior Broker' },
    { value: 'loan_officer', label: 'Loan Officer' },
    { value: 'branch_manager', label: 'Branch Manager' }
  ],
  nonprofits: [
    { value: 'executive_director', label: 'Executive Director' },
    { value: 'development_director', label: 'Development Director' },
    { value: 'program_director', label: 'Program Director' },
    { value: 'board_member', label: 'Board Member' }
  ],
  personal_trainers: [
    { value: 'gym_owner', label: 'Gym Owner' },
    { value: 'fitness_director', label: 'Fitness Director' },
    { value: 'head_trainer', label: 'Head Trainer' },
    { value: 'program_manager', label: 'Program Manager' }
  ],
  pest_control_services: [
    { value: 'company_owner', label: 'Company Owner' },
    { value: 'operations_manager', label: 'Operations Manager' },
    { value: 'service_manager', label: 'Service Manager' },
    { value: 'branch_manager', label: 'Branch Manager' }
  ],
  pet_groomers: [
    { value: 'shop_owner', label: 'Shop Owner' },
    { value: 'head_groomer', label: 'Head Groomer' },
    { value: 'salon_manager', label: 'Salon Manager' },
    { value: 'operations_manager', label: 'Operations Manager' }
  ],
  plumbers: [
    { value: 'company_owner', label: 'Company Owner' },
    { value: 'master_plumber', label: 'Master Plumber' },
    { value: 'service_manager', label: 'Service Manager' },
    { value: 'operations_manager', label: 'Operations Manager' }
  ],
  real_estate_agents: [
    { value: 'broker_owner', label: 'Broker Owner' },
    { value: 'team_leader', label: 'Team Leader' },
    { value: 'managing_broker', label: 'Managing Broker' },
    { value: 'sales_manager', label: 'Sales Manager' }
  ],
  restaurants: [
    { value: 'owner', label: 'Restaurant Owner' },
    { value: 'general_manager', label: 'General Manager' },
    { value: 'operations_director', label: 'Operations Director' },
    { value: 'executive_chef', label: 'Executive Chef' }
  ],
  retail_stores: [
    { value: 'store_owner', label: 'Store Owner' },
    { value: 'general_manager', label: 'General Manager' },
    { value: 'operations_manager', label: 'Operations Manager' },
    { value: 'district_manager', label: 'District Manager' }
  ],
  roofers: [
    { value: 'company_owner', label: 'Company Owner' },
    { value: 'operations_manager', label: 'Operations Manager' },
    { value: 'project_manager', label: 'Project Manager' },
    { value: 'estimator', label: 'Senior Estimator' }
  ],
  saas_startups: [
    { value: 'founder', label: 'Founder/CEO' },
    { value: 'cto', label: 'CTO' },
    { value: 'product_manager', label: 'Product Manager' },
    { value: 'marketing_director', label: 'Marketing Director' },
    { value: 'sales_director', label: 'Sales Director' }
  ],
  mental_health_therapists: [
    { value: 'practice_owner', label: 'Practice Owner' },
    { value: 'clinical_director', label: 'Clinical Director' },
    { value: 'lead_therapist', label: 'Lead Therapist' },
    { value: 'program_director', label: 'Program Director' }
  ],
  veterinarians: [
    { value: 'practice_owner', label: 'Practice Owner' },
    { value: 'medical_director', label: 'Medical Director' },
    { value: 'head_veterinarian', label: 'Head Veterinarian' },
    { value: 'hospital_manager', label: 'Hospital Manager' }
  ],
  // Default roles for any industry not specifically defined
  default: [
    { value: 'owner', label: 'Business Owner' },
    { value: 'ceo', label: 'CEO' },
    { value: 'director', label: 'Director' },
    { value: 'manager', label: 'Manager' }
  ]
};

export const COLD_EMAIL_FORM: FormDefinition = {
  id: 'cold-email',
  title: 'Cold Email Campaign',
  description: 'Generate a personalized cold email sequence based on industry research',
  fields: [
    {
      id: 'industry',
      label: 'Industry',
      type: 'select',
      required: true,
      options: industries
    },
    {
      id: 'role',
      label: 'Target Role',
      type: 'select',
      required: true,
      options: [], // Will be populated dynamically based on selected industry
      dependsOn: 'industry'
    },
    {
      id: 'offer',
      label: 'Offer Type',
      type: 'select',
      required: true,
      options: offerTypes
    },
    {
      id: 'customOffer',
      label: 'Custom Offer Details',
      type: 'textarea',
      required: false,
      placeholder: 'Describe your custom offer...',
      dependsOn: 'offer',
      showIf: (values) => values.offer === 'custom'
    },
    {
      id: 'tone',
      label: 'Tone of Voice',
      type: 'select',
      required: true,
      options: toneOptions
    },
    {
      id: 'additionalContext',
      label: 'Additional Context',
      type: 'textarea',
      required: false,
      placeholder: 'Add any additional context that could help generate better results (e.g., specific pain points, unique value propositions, target market characteristics, etc.)'
    }
  ]
};

export const LINKEDIN_DM_FORM: FormDefinition = {
  id: 'linkedin-dm',
  title: 'LinkedIn DM Campaign',
  description: 'Generate a LinkedIn outreach sequence with connection request and follow-ups',
  fields: [
    {
      id: 'industry',
      label: 'Industry',
      type: 'select',
      required: true,
      options: industries
    },
    {
      id: 'role',
      label: 'Target Role',
      type: 'select',
      required: true,
      options: [], // Will be populated dynamically based on selected industry
      dependsOn: 'industry'
    },
    {
      id: 'offer',
      label: 'Offer Type',
      type: 'select',
      required: true,
      options: offerTypes
    },
    {
      id: 'customOffer',
      label: 'Custom Offer Details',
      type: 'textarea',
      required: false,
      placeholder: 'Describe your custom offer...',
      dependsOn: 'offer',
      showIf: (values) => values.offer === 'custom'
    },
    {
      id: 'tone',
      label: 'Tone of Voice',
      type: 'select',
      required: true,
      options: toneOptions
    },
    {
      id: 'additionalContext',
      label: 'Additional Context',
      type: 'textarea',
      required: false,
      placeholder: 'Add any additional context that could help generate better results (e.g., specific pain points, unique value propositions, target market characteristics, etc.)'
    }
  ]
};

export const SOCIAL_MEDIA_POST_FORM: FormDefinition = {
  id: 'social-media-post',
  title: 'Social Media Post',
  description: 'Create optimized social media posts for various platforms',
  fields: [
    {
      id: 'platform',
      label: 'Platform',
      type: 'select',
      options: [
        { value: 'LinkedIn', label: 'LinkedIn' },
        { value: 'Twitter', label: 'Twitter' },
        { value: 'Facebook', label: 'Facebook' },
        { value: 'Instagram', label: 'Instagram' }
      ],
      required: true,
      placeholder: 'Select platform'
    },
    {
      id: 'postObjective',
      label: 'Post Objective',
      type: 'select',
      options: [
        { value: 'Drive Engagement', label: 'Drive Engagement' },
        { value: 'Build Awareness', label: 'Build Awareness' },
        { value: 'Generate Leads', label: 'Generate Leads' },
        { value: 'Drive Traffic', label: 'Drive Traffic' },
        { value: 'Establish Thought Leadership', label: 'Establish Thought Leadership' }
      ],
      required: true,
      placeholder: 'Select objective'
    },
    {
      id: 'targetAudience',
      label: 'Target Audience',
      type: 'textarea',
      required: true,
      placeholder: 'Describe your target audience'
    },
    {
      id: 'keyMessage',
      label: 'Key Message',
      type: 'textarea',
      required: true,
      placeholder: 'What\'s the main message or value proposition you want to convey?'
    }
  ]
};

export const FORM_DEFINITIONS: Record<string, FormDefinition> = {
  'cold-email': COLD_EMAIL_FORM,
  'linkedin-dm': LINKEDIN_DM_FORM,
  'social-media-post': SOCIAL_MEDIA_POST_FORM
}; 